import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import ErrorIcon from '~/meta/error.svg'
import { AppError, fromUnknownError } from '../../error'
import Loader from './loader'
import { useKeycloak } from '~/components/keycloak'
import keycloak from '~/utils/client/keycloak'
import { clear } from '~/prix/utils/browserCache'
import Modal from './modal'

const localNetworkAccessHelpSessionKey = 'prix-local-network-access-help-shown'

const Wrapper = styled.div<{ $errorTextColor?: string }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;

  .svg {
    display: flex;
  }
  .error-message {
    margin-bottom: 5px;
    margin-top: 20px;
    align-items: center;
    max-width: 1000px;
    small {
      font-size: 20px;
      color: ${({ $errorTextColor, theme }) => $errorTextColor ?? theme.colors.primary};
    }
    span {
      font-weight: bold;
      font-size: 28px;
      color: ${({ $errorTextColor, theme }) => $errorTextColor ?? theme.colors.primary};
    }
  }
`

const Button = styled.button`
  display: center;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  background-color: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 4px;
  align-self: center;
  padding: 0.5rem 2rem;
  margin: 0.625rem;
  transition-duration: 0.4s;
  border: 1px solid transparent;
  cursor: pointer;
  span {
    font-size: 16px;
    color: #fff;
  }
  &:hover {
    color: #fff;
    background-color: ${({ theme }) => theme.colors.secondary};
    border: 1px solid #fff;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.darkText};

  &:hover {
    background-color: ${({ theme }) => theme.colors.mediumGrey};
    color: ${({ theme }) => theme.colors.darkText};
    border: 1px solid transparent;
  }
`

const AccessLink = styled.a<{ $hoverColor?: string; $linkColor?: string }>`
  display: block;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ $linkColor, theme }) => $linkColor ?? theme.colors.primary};

  &:hover {
    color: ${({ $hoverColor, theme }) => $hoverColor ?? theme.colors.secondary};
  }
`

const LocalNetworkAccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  text-align: left;
  color: ${({ theme }) => theme.colors.darkText};

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.4rem;
    line-height: 1.25;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
  }

  ol {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0;
    padding-left: 20px;
    line-height: 1.45;
  }
`

const LocalNetworkAccessActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`

export interface HandleProps extends React.HTMLAttributes<HTMLDivElement> {
  isLoading?: boolean
  error?: Error | AppError | null | undefined
  onTryAgain?: () => Promise<void> | void
  tryAgainButtonLabel?: string
  loginAction?: boolean
  children?: React.ReactNode
  stateRoles?: boolean
  onLogout?: () => Promise<void> | void
  errorTextColor?: string
  accessLinkHoverColor?: string
  accessLinkColor?: string
}

export default function Handle({
  isLoading = false,
  error = null,
  children = null,
  onTryAgain,
  tryAgainButtonLabel = 'Tentar novamente',
  loginAction,
  stateRoles,
  onLogout,
  errorTextColor,
  accessLinkHoverColor,
  accessLinkColor,
  ...props
}: HandleProps) {
  const { logout, keycloak: keycloakFromContext } = useKeycloak()
  const [isLocalNetworkAccessHelpOpen, setIsLocalNetworkAccessHelpOpen] = useState(false)
  const [isTryingAgain, setIsTryingAgain] = useState(false)
  const isTryingAgainReference = useRef(false)

  const finalError = useMemo(() => {
    if (!error) {
      return null
    }
    return fromUnknownError(error)
  }, [error])
  const shouldShowErrorPrefix = useMemo(() => {
    if (!finalError) {
      return false
    }

    return (
      finalError.message !== 'Não autenticado' &&
      finalError.payload?.isAuthenticationRequired !== true
    )
  }, [finalError])
  const shouldShowLocalNetworkAccessHelp =
    finalError?.payload?.shouldShowLocalNetworkAccessHelp === true
  const currentTryAgainButtonLabel = shouldShowLocalNetworkAccessHelp
    ? 'Acessar'
    : tryAgainButtonLabel

  useEffect(() => {
    if (!shouldShowLocalNetworkAccessHelp) {
      setIsLocalNetworkAccessHelpOpen(false)
      return
    }

    try {
      if (sessionStorage.getItem(localNetworkAccessHelpSessionKey) === 'true') {
        return
      }

      sessionStorage.setItem(localNetworkAccessHelpSessionKey, 'true')
    } catch {
      // A ajuda continua disponível pelo botão mesmo sem sessionStorage.
    }

    setIsLocalNetworkAccessHelpOpen(true)
  }, [shouldShowLocalNetworkAccessHelp])

  const handleLogout = useCallback(async () => {
    if (onLogout) {
      await onLogout()
      return
    }

    if (keycloakFromContext) {
      await logout().catch(error => console.error(error))
      return
    }

    await clear().catch(error => console.error(error))
    await keycloak.logout({ redirectUri: window.location.origin }).catch(console.error)
  }, [keycloakFromContext, logout, onLogout])

  const handleTryAgainClick = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      if (isTryingAgainReference.current) {
        return
      }

      isTryingAgainReference.current = true
      setIsTryingAgain(true)

      try {
        if (finalError?.key === 'permission') {
          await handleLogout()
          return
        }

        if (!onTryAgain) {
          return
        }

        await onTryAgain()
      } catch (error) {
        console.error(error)
      } finally {
        isTryingAgainReference.current = false
        setIsTryingAgain(false)
      }
    },
    [finalError?.key, handleLogout, onTryAgain],
  )

  if (isLoading) {
    return <Wrapper {...props}>Carregando...</Wrapper>
  }
  if (!finalError) {
    return children as React.ReactElement
  }

  return (
    <Wrapper $errorTextColor={errorTextColor} {...props}>
      <div className='svg'>
        <ErrorIcon width={400} height={400} />
      </div>
      <div className='error-message'>
        {shouldShowErrorPrefix ? (
          <>
            <small>Ocorreu um erro</small>
            <br />
          </>
        ) : null}
        <span>{stateRoles ? error?.message : finalError.message}</span>
      </div>
      {onTryAgain && !stateRoles ? (
        <>
          <Button disabled={isTryingAgain} onClick={handleTryAgainClick}>
            {currentTryAgainButtonLabel}
          </Button>
          {shouldShowLocalNetworkAccessHelp ? (
            <SecondaryButton onClick={() => setIsLocalNetworkAccessHelpOpen(true)}>
              Ver instruções de rede local
            </SecondaryButton>
          ) : null}
          <AccessLink
            $hoverColor={accessLinkHoverColor}
            $linkColor={accessLinkColor}
            href='https://forms.office.com/r/K0K66VYKBV'
            target='_blank'
            rel='noreferrer'
          >
            Solicitar acesso
          </AccessLink>
        </>
      ) : null}
      {stateRoles ? <Button onClick={handleLogout}>Voltar ao login</Button> : null}
      {isLoading ? <Loader /> : null}
      {shouldShowLocalNetworkAccessHelp ? (
        <Modal
          isOpen={isLocalNetworkAccessHelpOpen}
          onClose={() => setIsLocalNetworkAccessHelpOpen(false)}
          customStyles={{
            overlay: {
              top: 0,
              left: 0,
            },
          }}
        >
          <LocalNetworkAccessContent>
            <h2>Libere a rede local no navegador</h2>
            <p>
              O Chrome pode bloquear o login quando a rede do SEBRAE exige permissão para
              comunicação local. Libere essa permissão e tente acessar novamente.
            </p>
            <ol>
              <li>Clique no ícone de cadeado ou configurações ao lado da URL.</li>
              <li>Ative a permissão “Rede local”.</li>
              <li>Recarregue a página e clique em “Acessar”.</li>
            </ol>
            <LocalNetworkAccessActions>
              <Button onClick={() => setIsLocalNetworkAccessHelpOpen(false)}>Entendi</Button>
            </LocalNetworkAccessActions>
          </LocalNetworkAccessContent>
        </Modal>
      ) : null}
    </Wrapper>
  )
}
